/**
 * upload/UploadOverlayReskin.tsx — the upload overlay in our Figma look (the opt-in skin).
 *
 * REAL-COMPONENT PORT. This is no longer hand-CSS markup approximating the Figma frames. It renders the
 * ACTUAL Design components (`~/FrontendFinal/Design/src`, ported verbatim into `./design/`) and feeds
 * the backend's live data into them as props — so the design's own JSX is the source of truth for every
 * pixel. The behaviour is unchanged: it calls the SAME `useUploadOverlay` hook and the SAME vm handlers
 * as classic; only the presentation is the design's.
 *
 * WHAT MAPS TO WHAT (see the Design page headers for each frame's measured derivation):
 *   frame P2 "Describe your tool"      → the Details stage (vm.stage === 'details')
 *   frame P3 "Detail your instructions"→ the Instructions stage (vm.stage === 'instructions')
 *   frame P4 "Review your writeup"     → the Review stage (vm.stage === 'review')
 *   frame P5 "Publish your tool"       → the done phase
 *   StepPills 4th step "Publish"       → the done phase; activeStep = vm.stageIndex + 1 while in-wizard
 *
 * FRAME. `OverlayFrame` (approach A) is the persistent bordered box + scrim, using the Design's verbatim
 * box classes, wired to the backend focus-trap (`vm.dialogRef` + `vm.onKeyDown`) and backdrop dismiss
 * (`props.onClose`). It provides the `WizardModalName` context the ported `ModalShell` reports its name
 * up to. Its root carries `.tb-ov-reskin`, now an inert marker only — the Tailwind reset and tokens are
 * GLOBAL, loaded once from src/design-system.css at app root (they used to be imported lazily from here,
 * which made the reset appear and disappear with the modal).
 *
 * CROSS-FADE. The Design's real motion: `AnimatePresence mode="wait"` keyed on the phase, each phase a
 * `ModalShell` whose `stepFadeIn` opacity cross-dissolve plays out-then-in. The box never moves.
 *
 * WHERE THE BACKEND IS RICHER THAN THE FRAME. The Figma frames draw 5 static Details fields and 4 static
 * Instructions rows with no values, selects, chips, validation, collapse, or dynamic component tree. The
 * backend has all of those and they are load-bearing (the gate, UP-F44 states, the per-component
 * explanations). So the Details form renders the design `FormField` primitive for every real field —
 * using its `children` slot for the three <select>s and the tags chip input — and the Instructions stage
 * renders the real dynamic component tree in design-token styling. No backend logic is reimplemented:
 * every value and handler is the vm's.
 *
 * NOT FROM A FRAME (extrapolations, flagged for the user to redesign): loading, resume, duplicates. They
 * are rendered as design-token cards inside a title-inset ModalShell (no step pills).
 */
import { AnimatePresence, motion } from 'motion/react';
// NOTE: the classic stage's per-step guidance copy (`promptFor`) and its collapse/branch/status
// affordances are NOT rendered in the reskin. The design's P3 instruction row is a bare
// FormField — label above a box — with no slot for a hint line, a collapse toggle, a branch tag
// or a per-row status badge. Rather than invent those treatments, the reskin draws the design's
// field and reports the gate through the design's own footer count + summary notice. If those
// affordances should return, they need designing in the Design app first.
import { OverlayFrame } from './design/OverlayFrame';
import { ModalShell } from './design/ModalShell';
import { WizardJourney } from './design/WizardJourney';
import { FormField } from './design/FormField';
import { IdentityCard } from './design/IdentityCard';
import { integrationsFrom } from './pages/vmAdapters';
import { PreviewCard } from './design/PreviewCard';
import { ReviewPreviewCard } from './design/ReviewPreviewCard';
import { ScrollColumn, type ScrollMetrics } from './design/ScrollColumn';
import { Button } from './design/Button';
import { TagsControl } from './design/TagsControl';
import { stepFadeIn } from './design/motion';
import { ATTESTATION_STATEMENT, RESPONSIBILITY_STATEMENT } from './ReviewStage';
import { LIMITS, type DetailsField } from './validation';
import { draftsAvailable, type UploadDraft } from './draftStore';
import type { DuplicateCandidate, UploadResult } from '../api';
import Kombi from '../../design/Kombi';
import {
  STAGE_LABELS,
  useUploadOverlay,
  type UploadOverlayProps,
  type UploadOverlayVm,
} from './useUploadOverlay';

export function UploadOverlayReskin(props: UploadOverlayProps) {
  const vm = useUploadOverlay(props);
  const { phase } = vm;

  // The AnimatePresence key: one string per distinct thing the builder can see, so a phase or stage
  // change plays the design's cross-fade. In-wizard the key is the stage, so Details→Instructions→Review
  // each cross-fade; the non-wizard phases key on their own name.
  const key = phase.kind === 'wizard' ? `wizard:${vm.stage}` : phase.kind;

  return (
    <OverlayFrame dialogRef={vm.dialogRef} onKeyDown={vm.onKeyDown} onClose={props.onClose}>
      <AnimatePresence mode="wait" initial={false}>
        {phase.kind === 'loading' && (
          <MessageShell key={key} label="Preparing your tool" bodyScroll={false}>
            <div
              className="flex min-h-0 flex-1 flex-col items-center justify-center gap-8"
              role="status"
            >
              {/* THE KOMBI REPLACES THE SPINNER HERE, and this is the one place in the product with a
                  genuine wait to cover: this phase fetches any saved draft and runs the duplicate
                  check before the wizard can open, so it is real latency rather than an invented
                  pause. `drive` loops because nobody knows how long it will be.

                  NO REDUCED-MOTION REGRESSION. The Spinner this replaces was a motion.div doing
                  `rotate: 360` on repeat, and App.tsx's <MotionConfig reducedMotion="user"> (layer 2
                  of the motion policy) already suppressed it — so a reduced-motion builder was
                  already reading the line below, not watching anything. The Kombi behaves the same
                  way: static illustration, same role="status", same sentence.

                  aria-hidden (the Kombi's default) is correct: role="status" on this container plus
                  the copy is what carries the state to assistive tech. */}
              <Kombi variant="band" />
              <p className="text-sm font-light text-ink">Reading the steps of this automation…</p>
            </div>
          </MessageShell>
        )}

        {phase.kind === 'unavailable' && (
          <MessageShell
            key={key}
            label="This automation can’t be shared"
            footer={
              <FooterRight>
                <Button variant="outline-square" size="xs" onClick={props.onClose}>
                  Close
                </Button>
              </FooterRight>
            }
          >
            <Notice tone="warn" role="alert">
              {phase.message}
            </Notice>
            <p className="mt-8 text-sm font-light text-ink">
              Nothing has been shared and nothing has been changed. Pick a different automation, or
              try again once the flow opens correctly in Power Automate.
            </p>
          </MessageShell>
        )}

        {phase.kind === 'resume' && (
          <MessageShell key={key} label="Resume your draft">
            <ResumeCard
              draft={phase.draft}
              componentCount={vm.components.length}
              onResume={vm.onResume}
              onStartNew={vm.onStartNew}
            />
          </MessageShell>
        )}

        {phase.kind === 'duplicates' && (
          <MessageShell key={key} label="Similar tools already exist">
            <DuplicateCard
              candidates={phase.candidates}
              sameFlow={phase.sameFlow}
              onContinue={vm.onDuplicatesContinue}
              onAbandon={vm.onDuplicatesAbandon}
            />
          </MessageShell>
        )}

        {phase.kind === 'wizard' && vm.stage === 'details' && (
          <DetailsShell key={key} vm={vm} onClose={props.onClose} />
        )}
        {phase.kind === 'wizard' && vm.stage === 'instructions' && (
          <InstructionsShell key={key} vm={vm} onClose={props.onClose} />
        )}
        {phase.kind === 'wizard' && vm.stage === 'review' && (
          <ReviewShell key={key} vm={vm} onClose={props.onClose} />
        )}

        {phase.kind === 'done' && (
          <MessageShell
            key={key}
            label="Your tool is shared"
            header={<WizardJourney activeStep={4} />}
            footer={
              <FooterRight>
                <Button variant="outline-square" size="xs" onClick={props.onClose}>
                  Close
                </Button>
              </FooterRight>
            }
          >
            <DoneCard result={phase.result} />
          </MessageShell>
        )}
      </AnimatePresence>
    </OverlayFrame>
  );
}

/* ============================================================================
   The wizard body: the P2 header rule beneath the step pills, shared by all
   three wizard stages. The rule is -mx'd back out of the --step-row-inset so it
   spans the modal's full width while the pills stay inset — verbatim from the
   Design pages.
   ============================================================================ */
function WizardHeader({ activeStep }: { activeStep: number }) {
  return (
    <>
      <WizardJourney activeStep={activeStep} />
      <div className="-mx-step-row mt-11 h-px bg-ink" />
    </>
  );
}

/**
 * The status region above a wizard body: the error banner and the "drafts unavailable" note, shown when
 * relevant.
 *
 * WHERE THIS MAY NOT GO — it used to be the FIRST CHILD of P2/P3's left panel, and that silently broke
 * the whole left column's geometry. The design measures `--wizard-rule-gap-panel` (41.5) from the header
 * rule to the "Your Identity" HEADING BOX TOP, and `--wizard-heading-gap` (16) from that heading to the
 * card. Both are flow measurements, so ANY node rendered before the heading adds its own height plus its
 * margin to every offset below it — pushing the heading, the identity card and the preview card down
 * together. With `mb-8` (20px) on a Notice, the entire column sat ~20px+ low whenever a draft could not
 * be saved, which is the state the reporter was looking at.
 *
 * So this returns null when there is nothing to say (NOT an empty fragment inside the column), and the
 * caller renders it OUTSIDE the two-column grid — see DetailsShell/DetailShell. That keeps the grid's
 * first flow child the heading, exactly as the frame draws it.
 */
function WizardStatus({ vm }: { vm: UploadOverlayVm }) {
  const showDraftWarning = !draftsAvailable();
  if (!vm.errorMsg && !showDraftWarning) return null;
  return (
    // `gap-3` between stacked notices instead of `mb-8` on each: a trailing bottom margin would
    // re-introduce the displacement this component exists to avoid.
    <div className="flex flex-col gap-3 pb-8">
      {vm.errorMsg ? (
        <Notice tone="warn" role="alert">
          {vm.errorMsg}
        </Notice>
      ) : null}
      {showDraftWarning ? (
        <Notice tone="warn" role="status">
          This browser will not let us save a draft, so your progress is not being kept. Finish in
          one go, or enable site data for this page.
        </Notice>
      ) : null}
    </div>
  );
}

/* ============================================================================
   DETAILS (frame P2). Two-column body: left identity+preview panel, 1px column
   rule track, right form column with a mt-auto footer. The right column carries
   all TEN backend fields (not the frame's 5) through the design FormField.
   ============================================================================ */
function DetailsShell({ vm, onClose }: { vm: UploadOverlayVm; onClose: () => void }) {
  const { details } = vm;
  const showErrors = vm.triedStage.details;
  const errorFor = (field: DetailsField): string | undefined =>
    showErrors ? vm.detailErrors[field] : undefined;
  const errId = (field: DetailsField) => `rk-err-${field}`;

  // The identity fields the design's IdentityCard draws, from the builder's user record (read-only —
  // identity is never an input; UP-F01).
  //
  // THREE PAIRS, because --identity-card-height (161) is derived from exactly three closing on that box.
  // The frame draws Name / Brand / Job Title; the session's equivalents are Name / Department / Job
  // Title. Job title was already on GET /api/me and simply never passed through, which left the card a
  // row short inside a box sized for three — the plate is the design's, so the fix is the missing data,
  // not a smaller box. Department and job title are Graph-sourced and nullable, so a sparse record still
  // renders fewer rows; the plate keeps its measured height either way, as the design intends.
  const identityFields = [
    { label: 'Name:', value: vm.props.builderName },
    // UP-F02: contact address, pre-filled read-only from the user record. Omitted when the record
    // has none, same as department.
    ...(vm.props.builderEmail ? [{ label: 'Contact:', value: vm.props.builderEmail }] : []),
    ...(vm.props.builderDepartment
      ? [{ label: 'Department:', value: vm.props.builderDepartment }]
      : []),
  ];

  // The plate's connector list. This USED to be four slots named `Connector 1..4` — a stand-in for the
  // dashed logo stubs the plate drew, on the (then true) grounds that the Vm carried only a
  // `connectorState` summary. Two things changed: the plate now holds generated art rather than stubs,
  // and `integrations.length` reaches that art as a connector COUNT, so four invented names would become
  // a fabricated measurement. `vm.components` is in memory by this stage — the pages under ./pages have
  // always read it this way — so the real distinct connectors are used, exactly as `integrationsFrom`
  // documents. A flow with none yields `[]`, which is the truthful answer rather than an empty plate.
  const previewSlots = integrationsFrom(vm.components);

  return (
    <ModalShell
      label="Describe your tool"
      headerInset="pills"
      header={<WizardHeader activeStep={1} />}
      // Same as P3: stationary rail + footer, the form column scrolls (this form
      // carries all TEN backend fields, so it overflows the frame's 640 height).
      bodyScroll={false}
    >
      {/* Status notices sit ABOVE the grid, never inside the left column — putting them in the column
          displaced the design's rule->heading->card rhythm. See the WizardStatus header. */}
      <WizardStatus vm={vm} />

      {/* `min-h-0 flex-1`, not `h-full` — see the fuller note on P3's grid. The ModalShell body is a
          flex column here, so this grid is a flex ITEM: a percentage height would resolve against an
          indefinite height (i.e. to auto) and let the grid outgrow the frame's fixed 639, hiding the
          footer and killing the scroll. min-h-0 lets the overflow resolve inside. */}
      <div className="grid min-h-0 flex-1 grid-cols-wizard-body">
        {/* LEFT PANEL — identity + preview, verbatim P2 rhythm. The FIRST flow child must be the
            heading, so `pt-wizard-rule-gap-panel` measures rule->heading as the frame does. */}
        <div className="flex min-w-0 flex-col pl-wizard-left-rest pr-wizard-left-gap pt-wizard-rule-gap-panel">
          <h2 className="text-sm font-light text-ink">Your Identity</h2>
          <IdentityCard className="mt-wizard-heading" fields={identityFields} />

          <h2 className="mt-wizard-group text-sm font-light text-ink">Tool Preview</h2>
          {/* The plate holds GENERATED art: a diagram of the flow being uploaded, drawn from
              `vm.components` — which is the manifest the walker already produced, so nothing is
              fetched and nothing is invented. `integrations` no longer paints logo stubs; it supplies
              the connector count the art falls back to when there is no readable structure yet. */}
          <PreviewCard
            className="mt-wizard-heading"
            name={details.name.trim() || vm.props.flow.name}
            owner={vm.props.environmentName}
            integrations={previewSlots}
            shape={vm.components}
          />
        </div>

        {/* The 1px column rule: the grid's middle track. */}
        <div className="bg-ink" aria-hidden="true" />

        {/* RIGHT COLUMN — the real 10-field form (scrolls), then a fixed footer.
            min-h-0 is required: a grid item defaults to min-height:auto, which refuses to shrink below
            its content, so without it the inner scroller can never resolve. */}
        <div className="flex min-h-0 min-w-0 flex-col pl-wizard-right pt-wizard-rule-gap-form">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-row-gap overflow-y-auto scrollbar-none pr-2">
            {/* Row 1: name + category, two-up at >=account (the frame's 343:318). */}
            <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form account:gap-x-col-gap account:gap-y-0">
              <FormField
                label="Tool name *"
                name="tool-name"
                required
                value={details.name}
                onChange={(v) => vm.onDetailsChange({ name: v })}
                registerRef={(el) => vm.registerDetailField('name', el)}
                {...(errorFor('name') ? { invalid: true, describedBy: errId('name') } : {})}
              />
              <FormField label="Primary category *" name="category">
                <SelectControl
                  id="category"
                  value={details.categoryId}
                  onChange={(v) => vm.onDetailsChange({ categoryId: Number(v) })}
                  registerRef={(el) => vm.registerDetailField('categoryId', el)}
                  options={vm.props.refData.categories}
                  invalid={!!errorFor('categoryId')}
                  describedBy={errorFor('categoryId') ? errId('categoryId') : undefined}
                />
              </FormField>
            </div>
            <FieldError show={!!errorFor('name')} id={errId('name')}>
              {errorFor('name')}
            </FieldError>
            <FieldError show={!!errorFor('categoryId')} id={errId('categoryId')}>
              {errorFor('categoryId')}
            </FieldError>

            <FormField
              label="What it does *"
              name="description"
              size="tall"
              value={details.description}
              onChange={(v) => vm.onDetailsChange({ description: v })}
              registerRef={(el) => vm.registerDetailField('description', el)}
              {...(errorFor('description')
                ? { invalid: true, describedBy: errId('description') }
                : {})}
            />
            <FieldError show={!!errorFor('description')} id={errId('description')}>
              {errorFor('description')}
            </FieldError>

            <FormField
              label="The problem it solves *"
              name="problem-solved"
              size="tall"
              value={details.problemSolved}
              onChange={(v) => vm.onDetailsChange({ problemSolved: v })}
              registerRef={(el) => vm.registerDetailField('problemSolved', el)}
              {...(errorFor('problemSolved')
                ? { invalid: true, describedBy: errId('problemSolved') }
                : {})}
            />
            <FieldError show={!!errorFor('problemSolved')} id={errId('problemSolved')}>
              {errorFor('problemSolved')}
            </FieldError>

            {/* Row: access + department, two-up. */}
            <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form account:gap-x-col-gap account:gap-y-0">
              <FormField label="Who may use it *" name="access">
                <SelectControl
                  id="access"
                  value={details.accessId}
                  onChange={(v) => vm.onDetailsChange({ accessId: Number(v) })}
                  registerRef={(el) => vm.registerDetailField('accessId', el)}
                  options={vm.props.refData.accessRequirements}
                  invalid={!!errorFor('accessId')}
                  describedBy={errorFor('accessId') ? errId('accessId') : undefined}
                />
              </FormField>
              <FormField label="Department it is for *" name="department">
                <SelectControl
                  id="department"
                  value={details.departmentId}
                  onChange={(v) => vm.onDetailsChange({ departmentId: Number(v) })}
                  registerRef={(el) => vm.registerDetailField('departmentId', el)}
                  options={vm.props.refData.departments}
                  invalid={!!errorFor('departmentId')}
                  describedBy={errorFor('departmentId') ? errId('departmentId') : undefined}
                />
              </FormField>
            </div>
            <FieldError show={!!errorFor('accessId')} id={errId('accessId')}>
              {errorFor('accessId')}
            </FieldError>
            <FieldError show={!!errorFor('departmentId')} id={errId('departmentId')}>
              {errorFor('departmentId')}
            </FieldError>

            {/* Time saved — the frame's left-track-only field. */}
            <div className="grid grid-cols-1 account:grid-cols-form account:gap-x-col-gap">
              <FormField
                label="Time saved each use (minutes) *"
                name="time-saved"
                type="number"
                value={details.estimatedTimeSaved}
                onChange={(v) => vm.onDetailsChange({ estimatedTimeSaved: v })}
                registerRef={(el) => vm.registerDetailField('estimatedTimeSaved', el)}
                {...(errorFor('estimatedTimeSaved')
                  ? { invalid: true, describedBy: errId('estimatedTimeSaved') }
                  : {})}
              />
            </div>
            <FieldError show={!!errorFor('estimatedTimeSaved')} id={errId('estimatedTimeSaved')}>
              {errorFor('estimatedTimeSaved')}
            </FieldError>

            <FormField
              label="Depends on *"
              name="dependencies"
              value={details.dependencies}
              onChange={(v) => vm.onDetailsChange({ dependencies: v })}
              registerRef={(el) => vm.registerDetailField('dependencies', el)}
              {...(errorFor('dependencies')
                ? { invalid: true, describedBy: errId('dependencies') }
                : {})}
            />
            <FieldError show={!!errorFor('dependencies')} id={errId('dependencies')}>
              {errorFor('dependencies')}
            </FieldError>

            <FormField
              label={vm.requirementsRequired ? 'Connector setup *' : 'Connector setup (optional)'}
              name="requirements"
              size="tall"
              value={details.requirements}
              onChange={(v) => vm.onDetailsChange({ requirements: v })}
              registerRef={(el) => vm.registerDetailField('requirements', el)}
              {...(errorFor('requirements')
                ? { invalid: true, describedBy: errId('requirements') }
                : {})}
            />
            <FieldError show={!!errorFor('requirements')} id={errId('requirements')}>
              {errorFor('requirements')}
            </FieldError>

            {/* Tags — the design draws no chips input, so the chips + input go in FormField's children. */}
            <FormField label={`Tags (optional, up to ${LIMITS.maxTags})`} name="tags">
              <TagsControl
                details={details}
                onChange={vm.onDetailsChange}
                suggestions={vm.props.refData.tags}
              />
            </FormField>
          </div>

          {/* FOOTER — pinned to the modal floor. Right-column-only rule + help line + buttons (P2). */}
          <div className="mt-auto pb-wizard-footer-b">
            <div className="-ml-wizard-right -mr-modal-x h-px bg-ink" />
            <div className="-mr-modal-x flex flex-wrap items-start justify-between gap-x-modal-footer gap-y-5 pr-modal-footer-r">
              <p className="min-w-0 pt-wizard-footer-help text-xs font-light text-ink">
                Shared by {vm.props.builderName}
                {vm.props.builderDepartment ? ` · ${vm.props.builderDepartment}` : ''} — taken from
                your user record.
              </p>
              <div className="flex shrink-0 items-start gap-modal-footer pt-wizard-footer-btn">
                <Button variant="outline-square" size="xs" onClick={onClose}>
                  Save and close
                </Button>
                <Button variant="outline-square" size="xs" onClick={vm.onNextFromDetails}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/* ============================================================================
   INSTRUCTIONS (frame P3). The frame draws 4 static action fields; the backend
   has a dynamic component tree with numbering, branch chips, collapse, and a
   per-component gate. We keep the real tree and dress it in design tokens.
   ============================================================================ */
function InstructionsShell({ vm, onClose }: { vm: UploadOverlayVm; onClose: () => void }) {
  const showErrors = vm.triedStage.instructions;
  const missing = new Set(vm.missingIds);

  // The same left-rail identity fields P2 draws (read-only, from the user record; UP-F01). Department
  // is omitted when the record has none — identical to DetailsShell, including the row-count mismatch
  // (see the long note there) that makes height="content" the right mode for this card.
  const identityFields = [
    { label: 'Name:', value: vm.props.builderName },
    // UP-F02: contact address, pre-filled read-only from the user record. Omitted when the record
    // has none, same as department.
    ...(vm.props.builderEmail ? [{ label: 'Contact:', value: vm.props.builderEmail }] : []),
    ...(vm.props.builderDepartment
      ? [{ label: 'Department:', value: vm.props.builderDepartment }]
      : []),
  ];

  // Same as P2 — the flow's real distinct connectors, not four invented names. See P2's note.
  const previewSlots = integrationsFrom(vm.components);

  return (
    <ModalShell
      label="Detail your instructions"
      headerInset="pills"
      header={<WizardHeader activeStep={2} />}
      // The left rail and the footer are stationary; the field column below owns
      // the scrolling. Without this the ModalShell body scrolls the whole
      // two-column region and the rail rides up with the fields.
      bodyScroll={false}
    >
      {/* Two-column body — identical chrome to P2 (DetailsShell): stationary left rail | 1px column
          rule track | right column. The Design's P3 (UploadDetailPage.tsx) shares P2's left panel and
          column rule verbatim; only the right column's contents differ (there: 4 static fields; here:
          the real dynamic component tree). The right column is the one that scrolls, so the left rail
          stays put — the faithful extension of the frame's "both panels stationary" for a dynamic list.

          `min-h-0 flex-1` rather than `h-full`: the ModalShell body is a flex column here, so the grid
          is a flex ITEM and takes its height from the frame's fixed 639. `h-full` would be a percentage
          against an indefinite height and resolve to auto, letting the grid outgrow the frame (which is
          what hid the footer and killed the scroll). min-h-0 lets the overflow resolve inside. */}
      {/* Above the grid, not inside the left column — see the WizardStatus header. */}
      <WizardStatus vm={vm} />

      <div className="grid min-h-0 flex-1 grid-cols-wizard-body">
        {/* LEFT PANEL — identity + preview, verbatim P2/P3 rhythm. The FIRST flow child must be the
            heading, so `pt-wizard-rule-gap-panel` measures rule->heading as the frame does. */}
        <div className="flex min-w-0 flex-col pl-wizard-left-rest pr-wizard-left-gap pt-wizard-rule-gap-panel">
          <h2 className="text-sm font-light text-ink">Your Identity</h2>
          <IdentityCard className="mt-wizard-heading" fields={identityFields} />

          <h2 className="mt-wizard-group text-sm font-light text-ink">Tool Preview</h2>
          <PreviewCard
            className="mt-wizard-heading"
            name={vm.details.name.trim() || vm.props.flow.name}
            owner={vm.props.environmentName}
            // See P2's note: the real connector list, and the art draws the real component tree.
            integrations={previewSlots}
            shape={vm.components}
          />
        </div>

        {/* The 1px column rule: the grid's middle track. */}
        <div className="bg-ink" aria-hidden="true" />

        {/* RIGHT COLUMN — the real component tree (scrolls), then a fixed footer at the floor.
            min-h-0 is required: a grid item defaults to min-height:auto, which refuses to shrink
            below its content, so without it the inner scroller can never resolve. */}
        <div className="flex min-h-0 min-w-0 flex-col pl-wizard-right pt-wizard-rule-gap-form">
          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-none pr-2">
            <p className="text-sm font-light text-ink">
              These are the actual steps of your automation, in the order they run. Explain each one
              so a colleague can set it up themselves — every step needs an explanation before you
              can continue.
            </p>

            {vm.droppedCount ? (
              <Notice tone="warn" role="status" className="mt-8">
                Your flow has changed since you saved this draft. {vm.droppedCount}{' '}
                {vm.droppedCount === 1 ? 'explanation' : 'explanations'} referred to steps that are
                no longer in it, so {vm.droppedCount === 1 ? 'it has' : 'they have'} been removed.
                Your other answers are intact.
              </Notice>
            ) : null}

            {showErrors && vm.missingIds.length > 0 ? (
              <Notice tone="warn" role="alert" className="mt-8">
                {vm.missingIds.length === 1
                  ? 'One step still needs an explanation.'
                  : `${vm.missingIds.length} steps still need an explanation.`}{' '}
                They are marked below.
              </Notice>
            ) : null}

            {/* THE INSTRUCTION FIELDS — the design's own P3 right column, verbatim: a
                `gap-row-gap` flex stack of `FormField size="action" required`, one per
                field. The only difference from the frame is the COUNT: the frame draws
                four static fields, this draws one per real step of the chosen flow. Label
                copy follows the frame's "Action N Instruction: <name>*" pattern, with N and
                the name coming from the flow's own component list. */}
            <div className="mt-8 flex min-w-0 flex-col gap-row-gap">
              {vm.components.map((component) => {
                const flagged = showErrors && missing.has(component.id);
                const errorId = `rk-comp-err-${component.order}`;
                return (
                  <div key={component.id} className="flex min-w-0 flex-col gap-field-gap">
                    <FormField
                      label={`Action ${component.numbering} Instruction: ${component.label}*`}
                      name={`rk-comp-${component.order}`}
                      size="action"
                      required
                      value={vm.explanations[component.id] ?? ''}
                      onChange={(v) => vm.onExplanationChange(component.id, v)}
                      // size="action" always renders a <textarea>, which is what the vm's field
                      // map holds; the guard narrows FormField's wider ref union without asserting.
                      registerRef={(el) =>
                        vm.registerComponentField(
                          component.id,
                          el instanceof HTMLTextAreaElement ? el : null,
                        )
                      }
                      {...(flagged ? { invalid: true, describedBy: errorId } : {})}
                    />
                    <FieldError show={flagged} id={errorId}>
                      Explain this step before continuing.
                    </FieldError>
                  </div>
                );
              })}
            </div>
          </div>

          {/* FOOTER — pinned to the modal floor. Right-column-only rule + help line + buttons (P2/P3). */}
          <div className="mt-auto pb-wizard-footer-b">
            <div className="-ml-wizard-right -mr-modal-x h-px bg-ink" />
            <div className="-mr-modal-x flex flex-wrap items-start justify-between gap-x-modal-footer gap-y-5 pr-modal-footer-r">
              <p className="min-w-0 pt-wizard-footer-help text-xs font-light text-ink">
                {vm.explained} of {vm.components.length} steps explained
              </p>
              <div className="flex shrink-0 items-start gap-modal-footer pt-wizard-footer-btn">
                <Button variant="outline-square" size="xs" onClick={() => vm.goToStage('details')}>
                  Back
                </Button>
                <Button variant="outline-square" size="xs" onClick={onClose}>
                  Save and close
                </Button>
                <Button variant="outline-square" size="xs" onClick={vm.onNextFromInstructions}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/* ============================================================================
   REVIEW (frame P4). Left: one large ReviewPreviewCard. Right: a ScrollColumn
   reading back Details + Instructions, with the two-tone scrollbar in the middle
   track. The attestation gate lives below the read-back.
   ============================================================================ */
function ReviewShell({ vm, onClose }: { vm: UploadOverlayVm; onClose: () => void }) {
  const { details } = vm;
  const showErrors = vm.triedStage.review;
  const attested = vm.draft?.attested ?? false;
  const takesResponsibility = vm.draft?.takesResponsibility ?? false;
  const labelOf = (options: { id: number; label: string }[], id: number): string =>
    options.find((o) => o.id === id)?.label ?? '—';

  return (
    <ModalShell
      label="Review your writeup"
      headerInset="pills"
      header={<WizardHeader activeStep={3} />}
      // Same as P2/P3: left panel + footer are stationary; the ScrollColumn owns its
      // own fixed h-review-card height. Without bodyScroll={false} the ModalShell body
      // is overflow-y-auto, and h-full on the inner flex container resolves to auto
      // (indefinite parent), letting the grid grow past the 640px frame and hiding the
      // mt-auto footer below the visible box.
      bodyScroll={false}
    >
      <div className="flex min-h-0 flex-1 min-w-0 flex-col">
        <div className="grid grid-cols-review-body">
          {/* LEFT — the large preview specimen. */}
          <div className="flex min-w-0 flex-col pt-wizard-rule-gap-form">
            {/* The largest box the flow is ever drawn in — 415x272, roughly twice the wizard
                plate's detail budget. Last step, so `vm.components` is certainly loaded. */}
            <ReviewPreviewCard
              name={details.name.trim() || vm.props.flow.name}
              owner={vm.props.environmentName}
              shape={vm.components}
            />
          </div>

          {/* SCROLLBAR + SCROLLING READ-BACK, one ScrollColumn. */}
          <ScrollColumn
            viewportClassName="h-review-card pl-review-form pt-wizard-rule-gap-form"
            scrollbar={renderScrollbar}
          >
            <div className="flex min-w-0 flex-col gap-row-gap pb-wizard-rule-gap-form">
              <WizardStatus vm={vm} />

              <SectionHeader label="Details" onEdit={() => vm.goToStage('details')} />
              <ReadBack label="Tool name" value={details.name} />
              <ReadBack label="What it does" value={details.description} />
              <ReadBack label="The problem it solves" value={details.problemSolved} />
              <ReadBack
                label="Primary category"
                value={labelOf(vm.props.refData.categories, details.categoryId)}
              />
              <ReadBack
                label="Who may use it"
                value={labelOf(vm.props.refData.accessRequirements, details.accessId)}
              />
              <ReadBack
                label="Department"
                value={labelOf(vm.props.refData.departments, details.departmentId)}
              />
              <ReadBack
                label="Time saved per use"
                value={`${details.estimatedTimeSaved} minutes`}
              />
              <ReadBack label="Depends on" value={details.dependencies} />
              <ReadBack
                label="Connector setup"
                value={details.requirements.trim() || 'Not provided'}
              />
              <ReadBack
                label="Tags"
                value={details.tags.length > 0 ? details.tags.join(', ') : 'None'}
              />

              <SectionHeader
                label={`Instructions — ${vm.components.length} ${vm.components.length === 1 ? 'step' : 'steps'}`}
                onEdit={() => vm.goToStage('instructions')}
              />
              {vm.components.map((component) => (
                <div
                  key={component.id}
                  className="flex min-w-0 flex-col gap-field-gap"
                  style={{ marginLeft: `${Math.min(component.depth, 5) * 0.9}rem` }}
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="text-xs font-medium text-ink">
                      {component.numbering}
                      {component.branch ? ` — ${component.branch}` : ''} — {component.label}
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-accent-link underline-offset-2 hover:underline"
                      onClick={() => vm.onEditComponent(component.id)}
                      aria-label={`Edit the explanation for step ${component.numbering}, ${component.label}`}
                    >
                      edit
                    </button>
                  </div>
                  <p className="text-xs font-light text-ink">
                    {vm.explanations[component.id]?.trim()}
                  </p>
                </div>
              ))}

              {/* DATA SAFETY — the one gate on submission (UP-F23), verbatim wording. */}
              <SectionHeader label="Data safety" />
              <p className="text-xs font-light text-ink">
                Every tool shared here is checked against the same rule about the data it touches.
              </p>
              <label className="flex items-start gap-3 text-xs font-light text-ink">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => vm.onAttestChange(e.target.checked)}
                  className="mt-1"
                  aria-invalid={showErrors && !attested ? true : undefined}
                  aria-describedby={showErrors && !attested ? 'rk-attest-err' : undefined}
                />
                <span>{ATTESTATION_STATEMENT}</span>
              </label>
              {showErrors && !attested ? (
                <span id="rk-attest-err" className="text-xs font-medium text-ink">
                  Confirm the data-safety statement to share this tool. If it does touch secret
                  data, or reaches outside the approved boundary, it cannot be shared here — talk to
                  the platform owner first.
                </span>
              ) : (
                <span className="text-xs font-light text-ink/60">
                  Your confirmation is recorded with your name and the time. A reviewer checks it
                  before the tool is approved.
                </span>
              )}

              {/* VERIFICATION — a SECOND, NON-BLOCKING choice (does not gate submission). Unticked, the
                  tool is held for the AI SWAT team to verify before it goes live; ticked, the builder
                  vouches for it and it publishes straight to Draft. Default is unticked (the safe,
                  verified route). It is deliberately NOT wired to the red-validation gate. */}
              <SectionHeader label="Verification" />
              <label className="flex items-start gap-3 text-xs font-light text-ink">
                <input
                  type="checkbox"
                  checked={takesResponsibility}
                  onChange={(e) => vm.onResponsibilityChange(e.target.checked)}
                  className="mt-1"
                />
                <span>{RESPONSIBILITY_STATEMENT}</span>
              </label>
              <span className="text-xs font-light text-ink/60">
                Leave this unticked and the AI SWAT team checks the tool for confidentiality and
                security before it is published. Tick it to take responsibility yourself and publish
                it as a draft straight away.
              </span>
            </div>
          </ScrollColumn>
        </div>

        {/* FULL-WIDTH FOOTER (P4). */}
        <div className="mt-auto pb-wizard-footer-b">
          <div className="-mx-modal-x h-px bg-ink" />
          <div className="-mr-modal-x flex items-start justify-end gap-modal-footer pr-modal-footer-r pt-wizard-footer-btn">
            <Button variant="outline-square" size="xs" onClick={() => vm.goToStage('instructions')}>
              Back
            </Button>
            <Button
              variant="outline-square"
              size="xs"
              onClick={onClose}
              {...(vm.submitting ? { state: 'disabled' as const } : {})}
            >
              Save and close
            </Button>
            <Button
              variant="outline-square"
              size="xs"
              onClick={() => void vm.onUpload()}
              {...(vm.submitting ? { state: 'disabled' as const } : {})}
            >
              {vm.submitting ? 'Sharing…' : 'Share this tool'}
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * The P4 two-tone scrollbar for the read-back column, handed to ScrollColumn. Verbatim from the Design's
 * UploadReviewPage: a faint full-height track with a solid thumb sized/positioned from the live metrics.
 */
function renderScrollbar(metrics: ScrollMetrics) {
  const thumbPct = `${(metrics.thumbFraction * 100).toFixed(4)}%`;
  const topPct = `calc(${(metrics.offsetFraction * 100).toFixed(4)}% * ${(
    1 - metrics.thumbFraction
  ).toFixed(6)})`;

  return (
    <div className="pt-wizard-rule-gap-form" aria-hidden="true">
      <div className="relative h-review-card w-px bg-rule-faint">
        <div className="absolute inset-x-0 bg-ink" style={{ height: thumbPct, top: topPct }} />
      </div>
    </div>
  );
}

/** A review section header with an optional Edit link (matches the classic ReviewStage's affordance). */
function SectionHeader({ label, onEdit }: { label: string; onEdit?: () => void }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-5">
      <h3 className="text-xs font-medium text-ink">{label}</h3>
      {onEdit ? (
        <button
          type="button"
          className="shrink-0 text-xs font-medium text-accent-link underline-offset-2 hover:underline"
          onClick={onEdit}
        >
          Edit
        </button>
      ) : null}
    </div>
  );
}

/** One read-back row in the Review column: a label above the entered value. */
function ReadBack({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-field-gap">
      <span className="text-xs font-medium text-ink">{label}</span>
      <span className="whitespace-pre-wrap break-words text-xs font-light text-ink">{value}</span>
    </div>
  );
}

/* ============================================================================
   Shared controls: the <select> and tags chip input the design frame has no
   primitive for, rendered inside FormField's children slot in design styling.
   ============================================================================ */
function SelectControl({
  id,
  value,
  onChange,
  registerRef,
  options,
  invalid,
  describedBy,
}: {
  id: string;
  value: number;
  onChange: (v: string) => void;
  registerRef: (el: HTMLSelectElement | null) => void;
  options: { id: number; label: string }[];
  invalid: boolean;
  describedBy: string | undefined;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      ref={registerRef}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy}
      className="h-field w-full min-w-0 rounded-md border border-border-subtle bg-transparent px-field-x text-xs font-light text-ink outline-none focus-visible:border-ring-focus"
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** A field-level error line, shown only when the field is flagged. */
function FieldError({
  show,
  id,
  children,
}: {
  show: boolean;
  id: string;
  children: React.ReactNode;
}) {
  if (!show) return null;
  return (
    <span id={id} className="text-xs font-medium text-ink">
      {children}
    </span>
  );
}

/* ============================================================================
   Non-frame extrapolations: a title-inset ModalShell wrapper, a notice banner,
   a spinner, and the resume/duplicate/done cards. Flagged for redesign.
   ============================================================================ */

/** A ModalShell for the phases with no step pills (loading/resume/duplicates/unavailable/done). */
function MessageShell({
  label,
  header,
  footer,
  children,
  bodyScroll = true,
}: {
  label: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Passed straight through to ModalShell. Default `true` keeps the long phases (resume, duplicates)
   * scrollable. Pass `false` for a phase whose content is short and wants to CENTRE in the modal:
   * ModalShell's scrolling body is `display: block`, so a `flex-1` child of it cannot stretch and
   * `justify-center` therefore centres inside the content's own height instead of the modal's — see
   * that component's note on exactly this trap. `false` makes the body a flex column and the
   * stretching works. Only the loading phase needs it.
   */
  bodyScroll?: boolean;
}) {
  return (
    <ModalShell
      label={label}
      bodyScroll={bodyScroll}
      {...(header ? { headerInset: 'pills' as const, header } : {})}
      {...(footer ? { footer } : {})}
    >
      <div className="flex min-h-0 flex-1 flex-col pt-wizard-rule-gap-form">{children}</div>
    </ModalShell>
  );
}

/** A right-aligned footer band for the message shells. */
function FooterRight({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-wizard-footer-b">
      <div className="-mx-modal-x h-px bg-ink" />
      <div className="-mr-modal-x flex items-start justify-end gap-modal-footer pr-modal-footer-r pt-wizard-footer-btn">
        {children}
      </div>
    </div>
  );
}

function Notice({
  tone = 'info',
  role,
  className = '',
  children,
}: {
  tone?: 'info' | 'warn';
  role?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role={role}
      className={[
        'rounded-md border px-field-x py-field-x text-xs font-light',
        tone === 'warn' ? 'border-control bg-accent-subtle text-ink' : 'border-subtle text-ink',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/** A branded spinner (opacity/rotation only; reduced-motion neutralises it via the scoped CSS). */
function ResumeCard({
  draft,
  componentCount,
  onResume,
  onStartNew,
}: {
  draft: UploadDraft;
  componentCount: number;
  onResume: () => void;
  onStartNew: () => void;
}) {
  const saved = draft.updatedAt ? new Date(draft.updatedAt) : null;
  const explainedSoFar = Object.values(draft.explanations).filter((v) => v.trim() !== '').length;

  return (
    <motion.div
      variants={stepFadeIn}
      initial="hidden"
      animate="visible"
      className="flex min-w-0 flex-col gap-8"
    >
      <Notice tone="warn">
        You have an unfinished draft for this automation. Resuming keeps everything you wrote;
        starting again clears this draft for this automation only.
      </Notice>
      <dl className="grid grid-cols-2 gap-x-col-gap gap-y-row-gap">
        <ReadBack
          label="Last saved"
          value={
            saved && !Number.isNaN(saved.valueOf()) ? saved.toLocaleString('en-GB') : 'Unknown'
          }
        />
        <ReadBack label="Name so far" value={draft.details.name.trim() || 'Not yet named'} />
        <ReadBack label="Steps explained" value={`${explainedSoFar} of ${componentCount}`} />
        <ReadBack label="Stopped on" value={STAGE_LABELS[draft.stage]} />
      </dl>
      <div className="flex flex-wrap gap-modal-footer">
        <Button variant="outline-square" size="sm" onClick={onResume}>
          Resume this draft
        </Button>
        <Button variant="outline-square" size="sm" onClick={onStartNew}>
          Start new
        </Button>
      </div>
    </motion.div>
  );
}

function DuplicateCard({
  candidates,
  sameFlow,
  onContinue,
  onAbandon,
}: {
  candidates: DuplicateCandidate[];
  sameFlow: boolean;
  onContinue: () => void;
  onAbandon: () => void;
}) {
  return (
    <motion.div
      variants={stepFadeIn}
      initial="hidden"
      animate="visible"
      className="flex min-w-0 flex-col gap-8 overflow-y-auto scrollbar-none"
    >
      <Notice tone="warn">
        {sameFlow
          ? 'This automation has already been shared. Have a look — if it does the same job, there is no need for a second copy.'
          : 'Some similar tools already exist. If one of them already does the job, colleagues are better served by improving it than by having two.'}
      </Notice>

      {candidates.map((c) => (
        <div
          key={c.toolId}
          className="flex min-w-0 flex-col gap-field-gap rounded-md border border-subtle px-field-x py-field-x"
        >
          <h4 className="text-sm font-medium text-ink">{c.name}</h4>
          <p className="text-xs font-light text-ink/60">
            {c.matchKind === 'same_flow' ? 'Same automation' : 'Similar'} · {c.trustLabel} ·{' '}
            {c.mine ? 'shared by you' : `shared by ${c.ownerName}`}
          </p>
          <p className="text-xs font-light text-ink">{c.description}</p>
          <a
            href={`/tools/${c.toolId}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-accent-link underline-offset-2 hover:underline"
          >
            Open this tool in a new tab
          </a>
        </div>
      ))}

      <div className="flex flex-wrap gap-modal-footer">
        <Button variant="outline-square" size="sm" onClick={onContinue}>
          Mine does something different — carry on
        </Button>
        <Button variant="outline-square" size="sm" onClick={onAbandon}>
          It does the same thing — don’t share mine
        </Button>
      </div>
    </motion.div>
  );
}

function DoneCard({ result }: { result: UploadResult }) {
  const connectors = Array.isArray(result.required_connectors)
    ? result.required_connectors.map((c) => c.apiName).join(', ') || 'None'
    : 'Requirements could not be read';
  // 'Pending' ⇒ held for the AI SWAT team to verify; 'Draft' ⇒ the builder took responsibility.
  const heldForSwat = result.publication_status === 'Pending';
  return (
    <motion.div
      variants={stepFadeIn}
      initial="hidden"
      animate="visible"
      className="flex min-h-0 flex-1 flex-col items-center gap-8"
      role="status"
    >
      <div
        className="flex h-13 w-13 items-center justify-center rounded-full bg-accent-subtle text-xl text-accent"
        aria-hidden="true"
      >
        ✓
      </div>
      <h3 className="text-lg font-medium text-ink">
        {heldForSwat ? 'Sent for verification' : 'Shared'}
      </h3>
      <p className="text-sm font-light text-ink">
        {heldForSwat
          ? 'Held with the AI SWAT team to check for confidentiality and security. Once they approve it, it is published as Approved. Your local draft has been cleared.'
          : 'Saved as a Draft. Your local draft has been cleared.'}
      </p>
      <dl className="grid w-full max-w-modal grid-cols-2 gap-x-col-gap gap-y-row-gap">
        <ReadBack label="Tool id" value={String(result.tool_id)} />
        <ReadBack label="Blueprint stored" value={result.has_clientdata ? 'Yes' : 'No'} />
        <ReadBack label="Steps documented" value={String(result.documented_component_count ?? 0)} />
        <ReadBack label="Connectors required" value={`${result.connector_count} — ${connectors}`} />
      </dl>
      <a
        href={`/tools/${result.tool_id}`}
        className="text-xs font-medium text-accent-link underline-offset-2 hover:underline"
      >
        Open the tool detail page
      </a>
    </motion.div>
  );
}
